import { FC } from 'react';
import { Easing, motion, useReducedMotion } from 'motion/react';
import Check from '../../assets/Check.svg?react';
import { getComplexityStyle } from './complexityStyles';

interface Props {
  /** Raw Learnosity complexity tag; unknown or missing values fall back to the neutral style. */
  complexity: string | undefined;
  /** The problem has been answered correctly and checked. */
  isComplete: boolean;
  /** The problem the student is looking at right now, drawn wider than the rest. */
  isCurrent?: boolean;
  /**
   * Pop the badge in when it appears. Off for dots that illustrate the goal rather than report
   * progress, where there is nothing to celebrate.
   */
  isAnimated?: boolean;
}

// Same overshoot curve as PracticeProblemsAchievementTooltip, so the experiment's celebratory moments
// share one motion language.
const CHECK_POP_EASE: Easing = [0.34, 1.56, 0.64, 1];
const CHECK_POP = { duration: 0.35, ease: CHECK_POP_EASE };

const SLOT_CLASS = 'tw-relative tw-flex tw-h-5 tw-items-center tw-justify-center tw-rounded-full';

// Check.svg is a self-contained 14px badge — green disc, white tick — so it brings its own color
// rather than inheriting one from the dot. Pulling it 4px out of the 20px slot lands its centre on
// the circle's 45° point, which is the dot's visual top-right corner.
const BADGE_CLASS = 'tw-pointer-events-none tw-absolute -tw-top-1 -tw-right-1 tw-h-3.5 tw-w-3.5';

/**
 * One segment of the practice-problems progress vocabulary, shared by the header's track and the
 * Skill Challenge criteria pill. `isCurrent` sets the width; `isComplete` adds the corner badge and
 * leaves the dot itself untouched, so the complexity color stays readable either way.
 */
export const ComplexityDot: FC<Props> = ({ complexity, isComplete, isCurrent = false, isAnimated = true }) => {
  const reduceMotion = useReducedMotion();
  const style = getComplexityStyle(complexity);

  const width = isCurrent ? 'tw-w-12' : 'tw-w-5';
  // The current segment keeps a transparent slot so its inner bar reads as the marker; every other
  // segment gets the pale halo.
  const background = isCurrent ? null : style.halo;

  return (
    <span className={[SLOT_CLASS, width, background].filter(Boolean).join(' ')}>
      <span className={`tw-rounded-full ${style.dot} ${isCurrent ? 'tw-h-3 tw-w-full' : 'tw-h-2 tw-w-2'}`} />

      {/* Mounted only once the problem is solved, which is what plays the pop. Navigating between
          problems only changes the slot's width, so the badge is never remounted and never replays. */}
      {isComplete && (
        <motion.span
          aria-hidden="true"
          initial={isAnimated ? { scale: 0 } : false}
          animate={{ scale: 1 }}
          transition={reduceMotion ? { duration: 0 } : CHECK_POP}
          className={BADGE_CLASS}
        >
          <Check className="tw-block tw-h-full tw-w-full" />
        </motion.span>
      )}
    </span>
  );
};
